import type { BadgeProps } from '@/components/ui/badge';
import { isDateOnlyPast } from '@/utils/locale';

export interface PortalStatus {
  /** Normalised key the pages branch on */
  key: string;
  label: string;
  variant: NonNullable<BadgeProps['variant']>;
}

export function jobStatus(status: string): PortalStatus {
  switch (status) {
    case 'scheduled': return { key: 'scheduled', label: 'Booked', variant: 'info' };
    case 'in_progress': return { key: 'in_progress', label: 'In progress', variant: 'primary-soft' };
    case 'on_hold': return { key: 'on_hold', label: 'On hold', variant: 'warning' };
    case 'quote_sent': return { key: 'quote_sent', label: 'Quote sent', variant: 'info' };
    case 'completed': return { key: 'completed', label: 'Completed', variant: 'success' };
    case 'cancelled': return { key: 'cancelled', label: 'Cancelled', variant: 'secondary' };
    default: return { key: status, label: status, variant: 'secondary' };
  }
}

/** Jobs still ahead of the customer (drive the "upcoming" ordering on Home). */
export function jobIsOpen(status: string): boolean {
  return status === 'scheduled' || status === 'in_progress' || status === 'on_hold' || status === 'quote_sent';
}

export function estimateStatus(
  row: { status: string; valid_until: string | null },
  timezone: string,
): PortalStatus {
  const awaiting = row.status === 'sent' || row.status === 'viewed';
  if (row.status === 'expired' || (awaiting && isDateOnlyPast(row.valid_until, timezone))) {
    return { key: 'expired', label: 'Expired', variant: 'warning' };
  }
  if (awaiting) return { key: 'awaiting', label: 'Awaiting your decision', variant: 'info' };
  if (row.status === 'accepted') return { key: 'accepted', label: 'Accepted', variant: 'success' };
  if (row.status === 'declined') return { key: 'declined', label: 'Declined', variant: 'destructive-soft' };
  return { key: row.status, label: row.status, variant: 'secondary' };
}

export function invoiceStatus(
  row: { status: string; due_date: string | null },
  formatShortDate: (value: string | null | undefined) => string,
): PortalStatus {
  switch (row.status) {
    case 'sent':
      return { key: 'sent', label: row.due_date ? `Due ${formatShortDate(row.due_date)}` : 'Due', variant: 'info' };
    case 'overdue': return { key: 'overdue', label: 'Overdue', variant: 'destructive-soft' };
    case 'paid': return { key: 'paid', label: 'Paid', variant: 'success' };
    case 'cancelled': return { key: 'cancelled', label: 'Cancelled', variant: 'secondary' };
    case 'refunded': return { key: 'refunded', label: 'Refunded', variant: 'secondary' };
    default: return { key: row.status, label: row.status, variant: 'secondary' };
  }
}
